import React from 'react';
import { Button, ScrollArea, ToggleGroup, ToggleGroupItem, Tooltip, TooltipProvider } from '@onward-delivery/design';
import { Info } from '@onward-delivery/design/icons';
import OrderSummaryLine from './OrderSummaryLine';
import { ALLOCATION_MODES } from './constants';
import { draftKey, draftTotals } from './utils';

const ALLOCATION_MODE_HELP = (
    <span className="block max-w-64">
        <strong>FIFO</strong> — Choose a quantity per SKU. The warehouse allocates the oldest matching forward-stocked
        units when the order is picked from storage.
        <br />
        <br />
        <strong>Serialized</strong> — Choose and allocate the exact units for the order now, by serial number.
    </span>
);

const OrderSummary = ({
    draft,
    mode,
    onModeChange,
    itemsByKey,
    onQtyChange,
    onSerialsChange,
    onRemove,
    onCreate,
    disabled,
    disabledReason,
    emptyText,
}) => {
    const { skuCount, unitCount } = draftTotals(draft);
    const lines = Array.from(draft.values());

    return (
        <div className="flex w-[412px] shrink-0 flex-col self-stretch pt-6">
            <div className="flex flex-col gap-3 border-b border-border px-6 pb-4">
                <div className="flex items-center justify-between">
                    <p className="text-[15px] font-bold text-foreground">Order</p>
                    <p className="text-xs font-semibold text-muted">
                        {skuCount} SKUs • {unitCount} units
                    </p>
                </div>
                <div className="flex items-center justify-center gap-2">
                    <ToggleGroup
                        type="single"
                        value={mode}
                        // Radix emits "" when the active item is clicked again; the
                        // order always has a mode, so ignore the deselect.
                        onValueChange={(next) => next && onModeChange(next)}
                    >
                        <ToggleGroupItem value={ALLOCATION_MODES.FIFO}>FIFO</ToggleGroupItem>
                        <ToggleGroupItem value={ALLOCATION_MODES.SERIALIZED}>Serialized</ToggleGroupItem>
                    </ToggleGroup>
                    {/* Radix's Tooltip.Root reads a provider context with no default,
                        so it throws unless one is mounted above it. Nothing else in the
                        app mounts one. */}
                    <TooltipProvider>
                        <Tooltip content={ALLOCATION_MODE_HELP}>
                            <button
                                type="button"
                                aria-label="What do FIFO and Serialized mean?"
                                className="shrink-0 text-muted transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                            >
                                <Info className="h-4 w-4" />
                            </button>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
            <ScrollArea className="min-h-0 flex-1">
                {lines.length === 0 ? (
                    <p className="px-6 py-6 text-[13px] text-muted">
                        No SKUs yet. Add sellable stock from the left to build the order.
                    </p>
                ) : (
                    lines.map((line) => {
                        const key = draftKey(line);
                        return (
                            <OrderSummaryLine
                                key={key}
                                line={line}
                                mode={mode}
                                options={itemsByKey.get(key) || []}
                                onQtyChange={onQtyChange}
                                onSerialsChange={onSerialsChange}
                                onRemove={onRemove}
                                emptyText={emptyText}
                            />
                        );
                    })
                )}
            </ScrollArea>
            <div className="flex flex-col gap-2 border-t border-border px-6 py-5">
                <Button className="h-12 w-full" onClick={onCreate} disabled={disabled}>
                    Create order →
                </Button>
                {disabled && disabledReason ? (
                    <p className="text-center text-[11px] text-muted">{disabledReason}</p>
                ) : null}
            </div>
        </div>
    );
};

export default OrderSummary;
